import {
    AfterViewInit,
    ChangeDetectorRef,
    Directive,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { TranslationKeyMap } from '../../coaching-insight.i18n';
import { allPropsNull } from '../models/coaching.global.model';
import { HideableRecord } from '../models/oa.history.model';
import { CoachingInsightWidgetService } from '../services/coaching-insight.widget.service';
import { CoachingService } from '../services/coaching.service';

// maintain a list of Team Member Metrics
@Directive()
export abstract class AbstractOAHistory<T extends HideableRecord>
    implements OnInit, AfterViewInit, OnDestroy, OnChanges
{
    @Input() performerId: number;
    @Input() numRecords: number;
    @Input() displayRecords: number;
    @Input() filterOptions: any;
    @Input() filters: any;

    @Output() displayedRecordsChange: EventEmitter<number> =
        new EventEmitter<number>();

    @Output() totalRecordsChange: EventEmitter<number> =
        new EventEmitter<number>();

    protected abstract allRecordsHidden: boolean;
    protected abstract hasPendingData: boolean;
    protected abstract defaultSortIndex: number;
    protected abstract failLoad: string; // error message on fail

    protected sortFields = [];
    i18n = TranslationKeyMap;

    protected abstract initData(): void;
    protected abstract showSubs(): void;
    protected abstract hideSubs(): void;

    protected placeholderText: string = this._translateService.instant(
        TranslationKeyMap._7448_PLEASE_SELECT_,
    );

    public data: T[];
    protected displayData: T[];
    protected sub: Subscription;

    constructor(
        protected _coachInsightService: CoachingInsightWidgetService,
        protected _coachingService: CoachingService,
        protected cd: ChangeDetectorRef,
        protected _translateService: TranslateService,
    ) {}

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnInit() {
        //
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnChanges(args: SimpleChanges) {
        // eslint-disable-next-line no-prototype-builtins
        if (args.hasOwnProperty('numRecords')) {
            this.initData();
        }
        // eslint-disable-next-line no-prototype-builtins
        if (args.hasOwnProperty('filters')) {
            this.applyFilters();
        }
        // eslint-disable-next-line no-prototype-builtins
        if (args.hasOwnProperty('displayRecords')) {
            this.emitDisplayStatus();
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngAfterViewInit() {
        this.hasPendingData = false;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnDestroy() {
        if (this.sub) {
            this.sub.unsubscribe();
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    iframe(eventtype: string, stateOrId: string | number) {
        this._coachingService.openIFrame(
            eventtype,
            stateOrId,
            this.performerId,
        );
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    getDisplayable() {
        if (this.data) {
            this.displayData = this.data.filter((f) => f.display === true);
        }

        this.emitDisplayStatus();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    emitDisplayStatus() {
        if (this.data) {
            this.totalRecordsChange.emit(this.data.length);
            this.displayedRecordsChange.emit(
                this.displayData.length < this.displayRecords
                    ? this.displayData.length
                    : this.displayRecords,
            );
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    sortByField(index: number) {
        this.sortFields = this.sortFields.map((d, idx) => {
            if (idx !== index) {
                d.clearDirection();
            }
            return d;
        });
        this.sortFields[index].toggleSort(this.data);

        this.getDisplayable();

        this.cd.detectChanges();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    sortByFieldDefault() {
        this.sortFields[this.defaultSortIndex].resetDefaultDirection();
        this.sortFields[this.defaultSortIndex].defaultSort(this.data);
        this.cd.detectChanges();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    applyFilters() {
        if (!this.data) return;

        this.allRecordsHidden = false;

        if (allPropsNull(this.filters)) {
            this.data = this.data.map((f: T) => {
                f.display = true;
                return f;
            });
        } else {
            this.data = this.data.map((f: T) => {
                let allmatched = 0;
                let allfilters = 0;

                f.display = true;

                for (const c in this.filters) {
                    const type = typeof this.filters[c];

                    if (type === 'object') continue;

                    if (this.filters[c] != null) {
                        allfilters++;
                    }
                    if (type) {
                        let newdata = null;
                        if (f[c] == null) continue;
                        if (f[c] instanceof Array && f[c].length) {
                            let val;
                            const typetest = typeof f[c][0];
                            if (typetest === 'number') {
                                val = Number(this.filters[c]);
                            } else if (typetest === 'string') {
                                val = this.filters[c].toString();
                            }
                            if (f[c].indexOf(val) > -1) {
                                allmatched++;
                            }
                        } else {
                            switch (type) {
                                case 'number':
                                    newdata = Number(f[c]);
                                    break;
                                case 'string':
                                    newdata = f[c].toString();
                                    break;
                            }
                            if (newdata === this.filters[c]) {
                                allmatched++;
                            }
                        }
                    }
                }
                if (allfilters === allmatched) {
                    f.display = true;
                    return f;
                }
                f.display = false;
                return f;
            });
        }

        const visible = this.data.filter((c) => c.display === true);
        if (visible.length === 0) {
            this.allRecordsHidden = true;
        }

        this.getDisplayable();

        this.cd.detectChanges();
    }
}
